import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck,
  SignOut,
  Trash,
  ArrowClockwise,
  UserCircle,
  IdentificationCard,
  WarningCircle,
  Copy,
  Check,
  PaperPlaneRight,
  X,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "craniel_admin_token";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const Login = ({ onAuth }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data.token;
      localStorage.setItem(STORAGE_KEY, token);
      onAuth(token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center px-5 grid-pattern"
    >
      <div className="grain-overlay" aria-hidden="true" />
      <form
        onSubmit={submit}
        className="w-full max-w-md border border-border bg-card/70 backdrop-blur-xl p-8 sm:p-10 relative"
      >
        <div className="absolute top-0 left-0 h-[2px] w-full bg-accent" />
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck size={28} weight="duotone" className="text-accent" />
          <span className="font-code text-xs uppercase tracking-[0.2em] text-accent">
            Admin Access
          </span>
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-none mb-3">
          Craniel Control
        </h1>
        <p className="font-body text-muted-foreground mb-8 text-sm">
          Enter your admin password to review submitted files.
        </p>

        <label className="block font-code text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
          Password
        </label>
        <input
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="w-full bg-background border border-border px-4 py-3 font-code text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors"
          placeholder="••••••••••••••••"
        />

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-4 flex items-center gap-2 font-code text-xs text-red-400"
          >
            <WarningCircle size={14} weight="duotone" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-accent text-accent-foreground px-6 py-4 font-code text-sm uppercase tracking-[0.15em] font-medium hover:bg-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Verifying..." : "Enter dashboard"}
        </button>
      </form>
    </div>
  );
};

const Thumb = ({ path, token, onClick }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/uploads/file/${path}?auth=${encodeURIComponent(token)}`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);

  if (error) {
    return (
      <div className="aspect-square border border-border bg-muted/20 flex items-center justify-center">
        <WarningCircle size={20} className="text-muted-foreground" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className="aspect-square border border-border bg-muted/10 animate-pulse" />
    );
  }
  return (
    <button
      type="button"
      onClick={() => onClick(url)}
      className="aspect-square border border-border overflow-hidden hover:border-accent transition-colors group"
    >
      <img
        src={url}
        alt=""
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
    </button>
  );
};

const Dashboard = ({ token, onSignOut }) => {
  const [data, setData] = useState({ submissions: [], totals: { client: 0, employee: 0 } });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ auth: token });
      if (filter !== "all") params.set("submission_type", filter);
      const res = await axios.get(`${API}/admin/submissions?${params}`);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onSignOut();
        return;
      }
      setError(err?.response?.data?.detail || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [token, filter, onSignOut]);

  useEffect(() => {
    load();
  }, [load]);

  const removeSubmission = async (id) => {
    if (!window.confirm("Archive this submission? Files will be hidden from the dashboard.")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}?auth=${encodeURIComponent(token)}`);
      setData((d) => ({ ...d, submissions: d.submissions.filter((s) => s.id !== id) }));
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to archive");
    }
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  const tabs = [
    { key: "all", label: "All", count: data.totals.client + data.totals.employee },
    { key: "client", label: "Clients", count: data.totals.client },
    { key: "employee", label: "Employees", count: data.totals.employee },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden="true" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={22} weight="duotone" className="text-accent" />
            <span className="font-display font-black tracking-tighter text-lg uppercase">
              Craniel<span className="text-accent">.</span>
              <span className="font-code font-normal text-xs uppercase tracking-[0.2em] text-muted-foreground ml-3">
                Admin
              </span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Refresh"
              title="Refresh"
            >
              <ArrowClockwise size={18} weight="bold" className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onSignOut}
              data-testid="admin-signout"
              className="inline-flex items-center gap-2 border border-border px-4 py-2 font-code text-xs uppercase tracking-[0.15em] hover:border-accent hover:text-accent transition-colors"
            >
              <SignOut size={14} weight="bold" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-none mb-2">
            Submissions
          </h1>
          <p className="font-body text-muted-foreground text-sm">
            Review uploaded files from clients and platform representatives.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 font-code text-xs uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
                filter === t.key
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
              <span className="ml-2 text-muted-foreground">[{t.count}]</span>
            </button>
          ))}
        </div>

        {error && (
          <div
            data-testid="admin-error"
            className="border border-red-500/50 bg-red-500/5 px-4 py-3 font-code text-xs text-red-400 mb-6"
          >
            {error}
          </div>
        )}

        {loading && data.submissions.length === 0 ? (
          <div className="border border-border p-16 text-center">
            <div className="inline-block h-6 w-6 border-2 border-current border-t-transparent rounded-full animate-spin text-muted-foreground" />
          </div>
        ) : data.submissions.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="border border-border p-16 text-center"
          >
            <p className="font-code text-xs uppercase tracking-[0.15em] text-muted-foreground">
              No submissions yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {data.submissions.map((s) => {
              const isEmp = s.submission_type === "employee";
              const Icon = isEmp ? IdentificationCard : UserCircle;
              return (
                <article
                  key={s.id}
                  data-testid={`submission-${s.id}`}
                  className={`border border-border ${isEmp ? "bg-accent/[0.03]" : "bg-card/30"} relative`}
                >
                  <div className={`absolute top-0 left-0 h-[2px] ${isEmp ? "w-full bg-accent" : "w-16 bg-foreground"}`} />
                  <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left: meta */}
                    <div className="lg:col-span-4">
                      <div className="flex items-center gap-3 mb-4">
                        <Icon size={26} weight="duotone" className={isEmp ? "text-accent" : "text-foreground"} />
                        <span className={`font-code text-[11px] uppercase tracking-[0.18em] ${isEmp ? "text-accent" : "text-muted-foreground"}`}>
                          {isEmp ? "Employee" : "Client"}
                        </span>
                      </div>
                      <p className="font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
                        Received
                      </p>
                      <p className="font-body text-sm mb-4">{formatDate(s.created_at)}</p>

                      {s.telegram_handle && (
                        <>
                          <p className="font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
                            Telegram
                          </p>
                          <div className="flex items-center gap-2 mb-4">
                            <a
                              href={`https://t.me/${s.telegram_handle.replace(/^@/, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-code text-sm text-accent hover:underline break-all inline-flex items-center gap-1"
                            >
                              {s.telegram_handle}
                              <PaperPlaneRight size={12} weight="fill" />
                            </a>
                            <button
                              onClick={() => copy(s.telegram_handle, s.id + "-tg")}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="Copy handle"
                            >
                              {copied === s.id + "-tg" ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                          </div>
                        </>
                      )}

                      {s.note && (
                        <>
                          <p className="font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
                            Note
                          </p>
                          <p className="font-body text-sm leading-relaxed whitespace-pre-wrap">
                            {s.note}
                          </p>
                        </>
                      )}

                      <div className="mt-6 flex items-center gap-3">
                        <button
                          onClick={() => removeSubmission(s.id)}
                          data-testid={`submission-delete-${s.id}`}
                          className="inline-flex items-center gap-2 font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-red-400 transition-colors"
                        >
                          <Trash size={13} weight="bold" />
                          Archive
                        </button>
                      </div>
                    </div>

                    {/* Right: thumbnails */}
                    <div className="lg:col-span-8">
                      <p className="font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
                        Files ({s.files.length})
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {s.files.map((f) => (
                          <Thumb
                            key={f.id}
                            path={f.storage_path}
                            token={token}
                            onClick={(u) => setLightbox({ url: u, name: f.original_filename })}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="admin-lightbox"
          className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 p-2 text-foreground hover:text-accent"
            aria-label="Close"
          >
            <X size={24} weight="bold" />
          </button>
          <div className="max-w-5xl w-full max-h-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt="" className="max-h-[80vh] max-w-full object-contain border border-border" />
            {lightbox.name && (
              <p className="font-code text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {lightbox.name}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  const signOut = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setToken(null);
  };

  if (!token) return <Login onAuth={setToken} />;
  return <Dashboard token={token} onSignOut={signOut} />;
}
